import { create } from "zustand";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

interface AuthState {
  session: Session | null;
  user: User | null;
  schoolName: string | null;
  fullName: string | null;
  loading: boolean;
  initialized: boolean;

  initialize: () => () => void;
  signUp: (
    email: string,
    password: string,
    schoolName: string,
    fullName: string,
  ) => Promise<{ error: string | null }>;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
}

function readProfile(user: User | null | undefined): {
  schoolName: string | null;
  fullName: string | null;
} {
  return {
    schoolName: user?.user_metadata?.school_name ?? null,
    fullName: user?.user_metadata?.full_name ?? null,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user: null,
  schoolName: null,
  fullName: null,
  loading: false,
  initialized: false,

  initialize: () => {
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        set({
          session,
          user: session?.user ?? null,
          ...readProfile(session?.user),
          initialized: true,
        });
      })
      .catch((error) => {
        console.error("Failed to load session:", error);
        set({
          session: null,
          user: null,
          schoolName: null,
          fullName: null,
          initialized: true,
        });
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      set({
        session,
        user: session?.user ?? null,
        ...readProfile(session?.user),
      });
    });

    return () => subscription.unsubscribe();
  },

  signUp: async (email, password, schoolName, fullName) => {
    set({ loading: true });
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { school_name: schoolName, full_name: fullName },
      },
    });
    set({ loading: false });
    return { error: error?.message ?? null };
  },

  signIn: async (email, password) => {
    set({ loading: true });
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    set({ loading: false });
    return { error: error?.message ?? null };
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Sign out failed:", error);
      return;
    }
    set({
      session: null,
      user: null,
      schoolName: null,
      fullName: null,
    });
  },

  resetPassword: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error?.message ?? null };
  },
}));
